import Button from 'components/Button';
import InteractiveTable, {
  Column as TableColumn,
} from 'components/InteractiveTable';
import { TransactionOperation } from 'further-types/lp/transaction';
import { numberToCurrencyString } from 'further-ui/utils';
import type { ReconcilableTransaction } from './useReconcilableTransactions';

type Props = {
  reconcilableTransactions: Array<ReconcilableTransaction>;
  onTransactionSelection: (selectedTransactionRowIds: Array<string>) => void;
  onSelectTransactionToSplit: (transactionRowId: string) => void;
};

const ReconcilableTransactions: React.FC<Props> = ({
  reconcilableTransactions,
  onTransactionSelection,
  onSelectTransactionToSplit,
}) => {
  const rows = reconcilableTransactions.map((t) => ({
    ...t,
    id: t.rowId,
  }));

  const selectedTransactionRowIds = rows
    .filter((t) => t.isSelected)
    .map((t) => t.rowId);

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      field: 'transactionDate',
      headerName: 'Date',
      renderCell: ({ row }) => row.transactionDate.toReadable(),
      flex: 1,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
    },
    {
      field: 'debit',
      headerName: 'Debit',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.operationType === TransactionOperation.Debit
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.transactionCurrency,
          },
        ),
      flex: 1,
    },
    {
      field: 'credit',
      headerName: 'Credit',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.operationType === TransactionOperation.Credit
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.transactionCurrency,
          },
        ),
      flex: 1,
    },
    {
      field: 'split',
      headerName: '',
      renderCell: ({ row }) =>
        row.id && selectedTransactionRowIds.includes(row.id) ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            fullWidth
            onClick={() => {
              if (row.id) {
                onSelectTransactionToSplit(row.id);
              }
            }}
          >
            Split
          </Button>
        ) : null,
      flex: 1,
    },
  ];

  return (
    <InteractiveTable
      key={JSON.stringify([...rows, ...selectedTransactionRowIds])}
      selectable
      onRowSelection={onTransactionSelection}
      selectedRows={selectedTransactionRowIds}
      id="manual-journal-reconcilable-transactions"
      // @ts-expect-error - We need to fix interactive table types
      columns={columns}
      rows={rows}
      disableSorting
      disableTableConfiguration
      fullHeight
    />
  );
};

export default ReconcilableTransactions;
